import {
  PortalVaultDocumentListResponseSchema,
  PortalVaultDownloadResponseSchema,
  PortalVaultFolderListResponseSchema,
  type PortalVaultDocument,
  type PortalVaultDownloadResponse,
  type PortalVaultFolder,
} from "@l30/shared";
import { useMutation, useQuery } from "@tanstack/react-query";

import { portalFetch } from "./portal-client.js";

/**
 * Portal-side vault hooks.
 *
 * Three surfaces:
 *   - `usePortalFolders()` — list of folders shared with this
 *     portal user's tenant. Drives the folder sidebar.
 *   - `usePortalDocuments(folderId?)` — list of documents in
 *     a shared folder, or across all shared folders.
 *   - `useDownloadDocument()` — fetches a signed URL and triggers
 *     the browser download. The URL expires in 5 minutes; if a
 *     download fails the UI re-runs the mutation.
 *
 * Every response is parsed against the SSOT schema in `@l30/shared`
 * at the fetch boundary — drift surfaces as `ApiContractError`.
 */

const PORTAL_VAULT_FOLDERS_KEY = ["portal", "vault", "folders"] as const;
const PORTAL_VAULT_DOCUMENTS_KEY = ["portal", "vault", "documents"] as const;

export function usePortalFolders() {
  return useQuery<{ folders: PortalVaultFolder[] }>({
    queryKey: PORTAL_VAULT_FOLDERS_KEY,
    queryFn: () => portalFetch("/portal/vault/folders", {}, PortalVaultFolderListResponseSchema),
    refetchOnWindowFocus: false,
  });
}

export function usePortalDocuments(folderId?: string) {
  const params = new URLSearchParams();
  if (folderId) params.set("folderId", folderId);
  const qs = params.toString();

  return useQuery<{ documents: PortalVaultDocument[]; total: number }>({
    queryKey: [...PORTAL_VAULT_DOCUMENTS_KEY, folderId ?? null],
    queryFn: () =>
      portalFetch(
        `/portal/vault/documents${qs ? `?${qs}` : ""}`,
        {},
        PortalVaultDocumentListResponseSchema,
      ),
    refetchOnWindowFocus: false,
  });
}

export function useDownloadDocument() {
  return useMutation<PortalVaultDownloadResponse, Error, string>({
    mutationFn: async (documentId: string) => {
      const response = await portalFetch(
        `/portal/vault/documents/${documentId}/download`,
        {},
        PortalVaultDownloadResponseSchema,
      );
      // Trigger the download via a programmatic anchor click rather
      // than `window.location.href = ...` (forbidden by the
      // no-window-location lint rule — full-page reload drops the
      // SPA query cache). Blob storage returns the binary directly
      // with the right Content-Disposition; the browser saves it
      // without ever leaving the portal.
      const anchor = document.createElement("a");
      anchor.href = response.url;
      anchor.download = response.filename;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return response;
    },
  });
}
